/*
 * project-category-List.tsx
 * This project category list Component is used for representing all the project category in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../../components/page-header/page-header";
import GenericPopover from "../../../../components/pop-over";
import useToast from "../../../../hooks/useToast";
import Utils from "../../../../utils";
import { TFilterModel } from "../../../../types/config";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  debounceTime,
  defaultFilterParams,
  PageSize,
} from "../../../../utils/constants";
import {
  Button,
  Drawer,
  Dropdown,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { TProjectCategory } from "../../../../types/config";
import CustomDrawer from "../../../../components/drawer";
import {
  GetConfigGroups,
  GetProjectCategoryList,
} from "../../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../../utils";
import ActionCard from "../../../../components/action-card/action-card";
import { UserContext } from "../../../../context";
import {
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../../components/configurable-columns";
import ProjectCategoryForm from "./project-category-form";

export default function ProjectCategoryList() {
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<TProjectCategory[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();

  const initialColumns: any[] = [
    {
      title: "Sr. No.",
      key: "serialNumber",
      width: "8%",
      render: (_: any, __: TProjectCategory, index: number) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TProjectCategory, b: TProjectCategory) =>
        a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: TProjectCategory) =>
        record.name?.includes(value as string),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      sorter: (a: TProjectCategory, b: TProjectCategory) =>
        a.description.localeCompare(b.description),
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      width: "4%",
      render: (item: TProjectCategory) => (
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          menu={{
            items: [
              Utils.isUserHasAccess(
                context.user?.privilegeList,
                ePrivileges.EDITPROJECTCATEGORY,
              ) && {
                key: "edit",
                label: (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <EditOutlined style={{ fontSize: 12 }} />
                        Edit
                      </div>
                    }
                    component={ProjectCategoryForm}
                    componentProps={{
                      id: item.id,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Edit Project Category"
                  />
                ),
              },
            ].filter(Boolean) as any,
          }}
        >
          <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    defaultFilterParams.searchText,
  );

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, [debouncedSearchText]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          ...filterParams,
          searchText: filterParams.searchText,
          pageSize: PageSize,
        },
      };

      const response = await post(GetProjectCategoryList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map(
          (item: TProjectCategory, index: number) => ({
            key: (item.id ?? "").toString(),
            name: item.name,
            description: item.description,
            id: item.id,
          }),
        );
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const nameFilters = Array.from(
          new Set(data.map((item: TProjectCategory) => item.name)),
        ).map((name) => ({ text: name, value: name }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: nameFilters };
            }
            return col;
          }),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  // Update search text state
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(filterParams.searchText);
    }, debounceTime); // Set debounce delay to 300 milliseconds

    return () => {
      clearTimeout(timer); // Clear the timer on each update
    };
  }, [filterParams.searchText]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Project Categories`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDPROJECTCATEGORY,
          ) ? (
            <CustomDrawer
              size="default"
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={ProjectCategoryForm} // Passing Service Partner Form component
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  navigate("/dashboard/project-master");
                  fetchData();
                  setFilterParams(defaultFilterParams);
                },
              }}
              title="Add New Project Category"
            />
          ) : null}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
          {/* <FontAwesomeIcon
          icon={faFilter}
          className="listFilterIcons"
        ></FontAwesomeIcon> */}
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
